import { Box, Flex, Text } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CommonAPI } from "../../../Apis/commonApi";
import { RfxAPI } from "../../../Apis/rfxAPI"; // Claude: RFX badge polling 03-03-2026
import actions from "../../../redux/Actions";
import CustomIconButton from "../../Common/CustomIconButton";
import AiChatDrawer from "../../Common/AiChatDrawer"; // Claude: floating AI drawer 24-02-2026
import { Buyerscreens } from "../../../Constants/data";

import {
  MdFormatListBulleted,
  MdOutlineCalendarMonth,
  MdCalendarMonth,
  MdOutlineFormatListBulleted,
  MdDashboard,
  MdOutlineDashboard,
  MdAssignment,
  MdOutlineAssignment,
  MdSearch,
  MdAutoAwesome,
  MdLogout,
  MdDynamicFeed,
} from "react-icons/md";
import { useToastWrapper } from "../../../Wrapper/toastWrapper";

type Props = {
  children: React.ReactNode;
};

const noFooter = [
  "/buyer/company",
  "/buyer/chat",
  "/buyer/notifications",
];

const BuyerLayout = ({ children }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { buyerMenu } = useSelector((state: any) => state);
  const { user } = useSelector((state: any) => state);
  const [authState, setAuthState] = useState(!!(user && user?.role === "Buyer"));
  // Claude: RFX badge — red dot when new responses arrive 03-03-2026
  const [showRfxBadge, setShowRfxBadge] = useState(false);
  const [lastRfxCount, setLastRfxCount] = useState(0);

  const dispatch = useDispatch();
  const { error } = useToastWrapper();

  // Claude: access control — blocked/inactive→login, unverified/resubmitted→profile only 21-03-2026
  useEffect(() => {
    if (user && user?.role === "Buyer") {
      const vStatus = user?.verificationStatus;
      if (vStatus === "blocked" || vStatus === "inactive") {
        dispatch(actions.user.logout());
        navigate("/login");
        return;
      }
      // Claude: unverified/resubmitted locked to profile page only 21-03-2026
      if ((vStatus === "unverified" || vStatus === "resubmitted") && location.pathname !== "/buyer/profile") {
        navigate("/buyer/profile");
        return;
      }
      setAuthState(true);
    } else {
      navigate("/login");
    }
  }, [user, location.pathname]);

  // Claude: unverified/resubmitted/provisional nav gate — show toast, block navigation 21-03-2026
  const vStatus = user?.verificationStatus;
  const isRestricted = vStatus === "unverified" || vStatus === "resubmitted";
  const isProvisional = vStatus === "provisional";
  const restrictedToast = () => {
    error(
      vStatus === "resubmitted"
        ? "Your profile is pending review. Contact support@aftr.network for any assistance."
        : "Please update your profile and submit. Contact support@aftr.network for any assistance."
    );
  };
  const provisionalToast = () => error("Profile is pending review and has provisional access only.");
  const handleNav = (screenName: string, path: string, beforeNav?: () => void) => {
    if (isRestricted) { restrictedToast(); return; }
    beforeNav?.();
    dispatch(actions.buyerMenu.set({ activeMenu: screenName, aiChatOpen: false }));
    navigate(path);
  };

  const fetchNotification = async () => {
    try {
      // dispatch(actions.notification.setLoading(true));
      const res = await CommonAPI.getNotifications();
      if (res.success) {
        const { notifications, unread } = res.data.data;

        dispatch(
          actions.notification.get({
            notifications,
            unreadNotifications: unread,
          })
        );
      }
    } catch (err) {
      console.log("Error getting notification", err);
    } finally {
      // dispatch(actions.notification.setLoading(false));
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotification();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Claude: N47 — auto-open AI chat drawer once per browser session 26-03-2026
  useEffect(() => {
    const key = "aftr_buyer_ai_opened";
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      dispatch(actions.buyerMenu.set({ aiChatOpen: true }));
    }
  }, []);

  // Claude: RFX badge polling — check for new responses every 5 min 03-03-2026
  const checkRfxBadge = async () => {
    try {
      const res = await RfxAPI.getMyRfxs();
      if (res.success) {
        const total = (res.data as any[]).reduce((sum, rfx) => sum + (rfx.responseCount ?? 0), 0);
        setLastRfxCount(total);
        const seen = parseInt(localStorage.getItem('buyer_rfx_seen') ?? '-1', 10);
        if (seen === -1) {
          localStorage.setItem('buyer_rfx_seen', String(total));
        } else if (total > seen) {
          setShowRfxBadge(true);
        }
      }
    } catch {}
  };

  useEffect(() => {
    checkRfxBadge();
    const interval = setInterval(checkRfxBadge, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Claude: close AI chat drawer on every nav tap 24-02-2026
  const MenuItemClick = (screenName: string) => {
    dispatch(actions.buyerMenu.set({ activeMenu: screenName, aiChatOpen: false }));
  };

  return (
    <>
      {/* Claude: Dark left/right borders matching nav bar and header 17-02-2026 */}
      <Flex
        maxW={"600px"}
        w="100%"
        position={"relative"}
        flexDirection="column"
        alignSelf={"center"}
        mx={"auto"}
        display={"flex"}
        minH={"100dvh"}
        maxH={"100dvh"}
        overflow={"auto"}
        borderLeft={"2px solid #0E122F"}
        borderRight={"2px solid #0E122F"}
        bg={"#FFF5EE"}
      >
        <Box
          flexGrow={1}
          overflow={"auto"}
          pb={"20px"}
          display={"flex"}
          mb={"65px"}
        >
          {authState && children}
        </Box>
        {/* Claude: Floating logout button — visible only for unverified/resubmitted users 21-03-2026 */}
        {isRestricted && (
          <Box
            style={{
              position: "fixed",
              bottom: "75px",
              width: "100%",
              maxWidth: "596px",
              display: "flex",
              justifyContent: "flex-start",
              paddingLeft: "16px",
              pointerEvents: "none",
            }}
          >
            <Flex
              w="44px"
              h="44px"
              borderRadius="50%"
              bg="#0E122F"
              alignItems="center"
              justifyContent="center"
              shadow="0px 4px 12px rgba(0,0,0,0.35)"
              cursor="pointer"
              style={{ pointerEvents: "auto" }}
              onClick={() => { dispatch(actions.user.logout()); navigate("/login"); }}
            >
              <MdLogout size={18} color="white" />
            </Flex>
          </Box>
        )}

        {/* Claude: Floating AI button — hidden for unverified/resubmitted/inactive users 21-03-2026 */}
        {/* Claude: pointerEvents none on container so it doesn't block hover/clicks on content below 03-03-2026 */}
        <Box
          style={{
            position: "fixed",
            bottom: "75px",
            width: "100%",
            maxWidth: "596px",
            display: ["unverified", "resubmitted", "inactive"].includes(user?.verificationStatus) ? "none" : "flex",
            justifyContent: "flex-end",
            paddingRight: "16px",
            pointerEvents: "none",
          }}
        >
          <Flex
            style={{ pointerEvents: "auto" }}
            w="44px"
            h="44px"
            borderRadius="50%"
            bg="#0E122F"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            shadow="0px 4px 12px rgba(0,0,0,0.35)"
            cursor="pointer"
            onClick={() => dispatch(actions.buyerMenu.set({
              activeMenu: buyerMenu.activeMenu,
              aiChatOpen: !buyerMenu.aiChatOpen,
            }))}
          >
            {/* Claude: AI label inside circle 25-02-2026 */}
            <MdAutoAwesome size={16} color="#FF5F12" />
            <Text fontSize="9px" color="white" fontWeight={600} lineHeight={1}>AI</Text>
          </Flex>
        </Box>

        {/* Claude: AI Chat drawer — auto-opens on Dashboard, toggled by floating button 24-02-2026 */}
        <AiChatDrawer role="Buyer" />

        {noFooter.includes(location.pathname) ? null : (
          
          <Box
            // w={"100%"}
            bg={"white"}
            shadow={"0px 0px 10px rgba(0, 0, 0, 0.1)"}
            // Claude: Dark top border, fits inside layout side borders 17-02-2026
            borderTop={"2px solid #0E122F"}
            style={{
              position: "fixed",
              bottom: 0,
              width: "100%",
              maxWidth: "596px",
              zIndex: 102,
            }}
          >
            {/* Claude: 5-slot nav (20% each) — AI Chat first, Dashboard second 14-03-2026 */}
            <Flex h={"65px"}>
              {/* Claude: Discover first slot (new home), Feeds second slot 02-Apr-2026 */}
              <CustomIconButton
                width={"20%"}
                label={"Discover"}
                active={buyerMenu?.activeMenu == Buyerscreens.RFX}
                buttonBg={buyerMenu?.activeMenu == Buyerscreens.RFX ? "primary.main" : "transparent"}
                icon={<MdSearch size={24} color={buyerMenu?.activeMenu == Buyerscreens.RFX ? "#FF5F12" : "#1C1B1F"} />}
                showBadge={showRfxBadge}
                onClick={() => handleNav(Buyerscreens.RFX, "/buyer/rfxs", () => {
                  setShowRfxBadge(false);
                  localStorage.setItem('buyer_rfx_seen', String(lastRfxCount));
                })}
              />
              <CustomIconButton
                width={"20%"}
                label={"Feeds"}
                active={buyerMenu?.activeMenu == Buyerscreens.FEED}
                buttonBg={buyerMenu?.activeMenu == Buyerscreens.FEED ? "primary.main" : "transparent"}
                icon={<MdDynamicFeed size={24} color={buyerMenu?.activeMenu == Buyerscreens.FEED ? "#FF5F12" : "#1C1B1F"} />}
                onClick={() => handleNav(Buyerscreens.FEED, "/buyer/feeds")}
              />
              {/* Claude: Dashboard moved to 3rd slot 27-03-2026 */}
              <CustomIconButton
                width={"20%"}
                label={"Dashboard"}
                active={buyerMenu?.activeMenu == Buyerscreens.HOME}
                buttonBg={buyerMenu?.activeMenu == Buyerscreens.HOME ? "primary.main" : "transparent"}
                icon={buyerMenu?.activeMenu == Buyerscreens.HOME
                  ? <MdDashboard size={24} color={"#FF5F12"} />
                  : <MdOutlineDashboard size={24} color={"#1C1B1F"} />}
                onClick={() => handleNav(Buyerscreens.HOME, "/buyer")}
              />
              <CustomIconButton
                width={"20%"}
                label={"Sellers"}
                active={buyerMenu?.activeMenu == Buyerscreens.DETAILS}
                buttonBg={buyerMenu?.activeMenu == Buyerscreens.DETAILS ? "primary.main" : "transparent"}
                icon={buyerMenu?.activeMenu == Buyerscreens.DETAILS
                  ? <MdFormatListBulleted size={24} color={"#FF5F12"} />
                  : <MdOutlineFormatListBulleted size={24} color={"#1C1B1F"} />}
                onClick={() => handleNav(Buyerscreens.DETAILS, "/buyer/list")}
              />
              <CustomIconButton
                width={"20%"}
                label={"Meetings"}
                active={buyerMenu?.activeMenu == Buyerscreens.CALENDAR}
                buttonBg={buyerMenu?.activeMenu == Buyerscreens.CALENDAR ? "primary.main" : "transparent"}
                icon={buyerMenu?.activeMenu == Buyerscreens.CALENDAR
                  ? <MdCalendarMonth size={24} color={"#FF5F12"} />
                  : <MdOutlineCalendarMonth size={24} color={"#1C1B1F"} />}
                onClick={() => {
                  if (isProvisional) { provisionalToast(); return; }
                  handleNav(Buyerscreens.CALENDAR, "/buyer/calendar");
                }}
              />
            </Flex>
          </Box>
        )}
      </Flex>
    </>
  );
};

export default BuyerLayout;
